// app/javascript/components/AdminGameForm.jsx
import React, { useEffect, useRef, useState } from "react";
import AdminNav from "./AdminNav";

export default function AdminGameForm({ initialGame = {} }) {
  const isEdit = Boolean(initialGame.id);
  const descriptionInputRef = useRef(null);
  const trixEditorRef = useRef(null);

  const contentRatingInputRef = useRef(null);
  const contentRatingEditorRef = useRef(null);

  const acknowledgementInputRef = useRef(null);
  const acknowledgementEditorRef = useRef(null);

  const howToPlayInputRef = useRef(null);
  const howToPlayEditorRef = useRef(null);

  const cleanDescription = (description) => {
    if (!description) return "";

    if (typeof description === "string") {
      return description;
    }

    if (typeof description.body === "string") {
      return description.body;
    }

    return "";
  };

  const [game, setGame] = useState({
    name: initialGame.name || "",
    summary: initialGame.summary || "",
    content_rating: cleanDescription(initialGame.content_rating),
    game_controls: cleanDescription(initialGame.game_controls),
    acknowledgement: cleanDescription(initialGame.acknowledgement),
    description: cleanDescription(initialGame.description),
    url: initialGame.url || "",
    active: initialGame.active || false,
    sort_order: initialGame.sort_order ?? 0,
    id: initialGame.id || null,
    thumbnail_url: initialGame.thumbnail_url || "",
    header_image_url: initialGame.header_image_url || "",
    slug: initialGame.slug || "",
  });

  useEffect(() => {
    const cleanups = [
      loadTrixContent(trixEditorRef, game.description),
      loadTrixContent(contentRatingEditorRef, game.content_rating),
      loadTrixContent(howToPlayEditorRef, game.game_controls),
      loadTrixContent(acknowledgementEditorRef, game.acknowledgement),
    ];

    return () => {
      cleanups.forEach((cleanup) => {
        if (cleanup) cleanup();
      });
    };
  }, [game.id]);

  const [thumbnail, setThumbnail] = useState(null);
  const [headerImage, setHeaderImage] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setGame((g) => ({
      ...g,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const renderCurrentImage = (imageUrl, altText) => {
    if (!imageUrl) return null;

    return (
      <div className="mb-2">
        <div className="form-text mb-1">Current image</div>
        <img
          src={imageUrl}
          alt={altText}
          className="img-thumbnail"
          style={{ maxWidth: "240px", maxHeight: "160px", objectFit: "cover" }}
        />
      </div>
    );
  };

  const loadTrixContent = (editorRef, html) => {
    const editor = editorRef.current;
    if (!editor || !html) return undefined;

    const loadContent = () => {
      editor.editor.loadHTML(html);
    };

    if (editor.editor) {
      loadContent();
    } else {
      editor.addEventListener("trix-initialize", loadContent, { once: true });
    }

    return () => {
      editor.removeEventListener("trix-initialize", loadContent);
    };
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const endpoint = isEdit
      ? `/api/admin/games/${game.id}`
      : `/api/admin/games`;
    const method = isEdit ? "PATCH" : "POST";

    const formData = new FormData();

    const submittedDescription = descriptionInputRef.current?.value;
    formData.append("game[name]", game.name);
    formData.append("game[summary]", game.summary);
    formData.append(
      "game[content_rating]",
      contentRatingInputRef.current?.value || "",
    );
    formData.append(
      "game[game_controls]",
      howToPlayInputRef.current?.value || "",
    );
    formData.append(
      "game[description]",
      descriptionInputRef.current?.value || "",
    );
    formData.append(
      "game[acknowledgement]",
      acknowledgementInputRef.current?.value || "",
    );

    formData.append("game[url]", game.url);
    formData.append("game[active]", game.active);
    formData.append("game[sort_order]", game.sort_order);

    if (thumbnail) {
      formData.append("game[thumbnail]", thumbnail);
    }

    if (headerImage) {
      formData.append("game[header_image]", headerImage);
    }
    formData.append("game[slug]", game.slug);

    fetch(endpoint, {
      method,
      credentials: "include",
      headers: {
        "X-CSRF-Token": document.querySelector("[name=csrf-token]").content,
      },
      body: formData,
    })
      .then((r) => {
        if (!r.ok) throw new Error("Save failed");
        return r.json();
      })
      .then(() => {
        window.location.href = "/admin/games";
      })
      .catch((err) => alert(err.message));
  };

  return (
    <div>
      <AdminNav />
      <main className="container my-4">
        <div className="card">
          <div className="card-header">
            <h2 className="mb-0">{isEdit ? "Edit Game" : "New Game"}</h2>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              {/* Active */}
              <div className="form-check mb-4">
                <input
                  id="active"
                  name="active"
                  type="checkbox"
                  className="form-check-input"
                  checked={game.active}
                  onChange={handleChange}
                />
                <label htmlFor="active" className="form-check-label">
                  Active
                </label>
              </div>

              {/* Sort Order */}
              <div className="mb-3">
                <label htmlFor="sort_order" className="form-label">
                  Sort Order
                </label>
                <input
                  id="sort_order"
                  name="sort_order"
                  type="number"
                  className="form-control"
                  value={game.sort_order}
                  onChange={handleChange}
                />
              </div>

              {/* Name */}
              <div className="mb-3">
                <label htmlFor="name" className="form-label">
                  Name
                </label>
                <input
                  id="name"
                  name="name"
                  className="form-control"
                  value={game.name}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Slug */}
              <div className="mb-3">
                <label htmlFor="slug" className="form-label">
                  Slug
                </label>
                <input
                  id="slug"
                  name="slug"
                  className="form-control"
                  value={game.slug}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Summary */}
              <div className="mb-3">
                <label htmlFor="summary" className="form-label">
                  Catalog Summary
                </label>
                <textarea
                  id="summary"
                  name="summary"
                  className="form-control"
                  rows="2"
                  maxLength="180"
                  value={game.summary}
                  onChange={handleChange}
                />
                <div className="form-text">
                  Short text shown on the game catalog card.
                </div>
              </div>

              {/* Content Rating */}
              <div className="mb-3">
                <label htmlFor="game_content_rating" className="form-label">
                  Content Rating
                </label>

                <input
                  id="game_content_rating"
                  ref={contentRatingInputRef}
                  type="hidden"
                  name="game[content_rating]"
                  defaultValue={game.content_rating}
                />

                <trix-editor
                  ref={contentRatingEditorRef}
                  input="game_content_rating"
                  className="trix-content"
                  data-direct-upload-url="/rails/active_storage/direct_uploads"
                  data-blob-url-template="/rails/active_storage/blobs/redirect/:signed_id/:filename"
                />
              </div>

              {/* Game Controls */}
              <div className="mb-3">
                <label htmlFor="game_game_controls" className="form-label">
                  Game Controls
                </label>

                <input
                  id="game_game_controls"
                  ref={howToPlayInputRef}
                  type="hidden"
                  name="game[game_controls]"
                  defaultValue={game.game_controls}
                />

                <trix-editor
                  ref={howToPlayEditorRef}
                  input="game_game_controls"
                  className="trix-content"
                  data-direct-upload-url="/rails/active_storage/direct_uploads"
                  data-blob-url-template="/rails/active_storage/blobs/redirect/:signed_id/:filename"
                />
              </div>

              {/* Description */}
              <div className="mb-3">
                <label htmlFor="game_description" className="form-label">
                  Description
                </label>

                <input
                  id="game_description"
                  ref={descriptionInputRef}
                  type="hidden"
                  name="game[description]"
                  defaultValue={game.description}
                />

                <trix-editor
                  ref={trixEditorRef}
                  input="game_description"
                  className="trix-content"
                  data-direct-upload-url="/rails/active_storage/direct_uploads"
                  data-blob-url-template="/rails/active_storage/blobs/redirect/:signed_id/:filename"
                />

                <div className="form-text">
                  Full rich text description shown on the game detail page.
                </div>
              </div>

              {/* acknowledgement */}
              <div className="mb-3">
                <label htmlFor="game_acknowledgement" className="form-label">
                  Acknowledgement
                </label>

                <input
                  id="game_acknowledgement"
                  ref={acknowledgementInputRef}
                  type="hidden"
                  name="game[acknowledgement]"
                  defaultValue={game.acknowledgement}
                />

                <trix-editor
                  ref={acknowledgementEditorRef}
                  input="game_acknowledgement"
                  className="trix-content"
                  data-direct-upload-url="/rails/active_storage/direct_uploads"
                  data-blob-url-template="/rails/active_storage/blobs/redirect/:signed_id/:filename"
                />
              </div>

              {/* URL */}
              <div className="mb-3">
                <label htmlFor="url" className="form-label">
                  URL
                </label>
                <input
                  id="url"
                  name="url"
                  type="url"
                  className="form-control"
                  value={game.url}
                  onChange={handleChange}
                />
              </div>

              {/* Thumbnail */}
              <div className="mb-3">
                <label htmlFor="thumbnail" className="form-label">
                  Thumbnail
                </label>
                {renderCurrentImage(
                  game.thumbnail_url,
                  `${game.name || "Game"} thumbnail`,
                )}
                <input
                  id="thumbnail"
                  name="thumbnail"
                  type="file"
                  accept="image/*"
                  className="form-control"
                  onChange={(e) => setThumbnail(e.target.files[0])}
                />
              </div>

              {/* Header Image */}
              <div className="mb-3">
                <label htmlFor="header_image" className="form-label">
                  Header Image
                </label>
                {renderCurrentImage(
                  game.header_image_url,
                  `${game.name || "Game"} header image`,
                )}
                <input
                  id="header_image"
                  name="header_image"
                  type="file"
                  accept="image/*"
                  className="form-control"
                  onChange={(e) => setHeaderImage(e.target.files[0])}
                />
              </div>

              <button type="submit" className="btn btn-primary">
                {isEdit ? "Update Game" : "Create Game"}
              </button>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
